import type { PlayEvent, Region } from '@court-sync/shared';
import { formatCurrency, getCurrencyFromRegion } from '@/utils/currency';

export function formatPlayEventPriceCents(
  cents: number | null | undefined,
  region?: Region | string | null
): string | null {
  if (cents == null) return null;
  const currency = getCurrencyFromRegion(
    typeof region === 'string' ? (region as Region) : (region ?? undefined)
  );
  return formatCurrency(cents / 100, currency);
}

/** Lowest non-null price tier for public list cards. */
export function getPlayEventFromPriceLabel(
  event: Pick<PlayEvent, 'memberPriceCents' | 'studentPriceCents' | 'guestPriceCents'>,
  region?: Region | string | null
): string | null {
  const prices = [event.memberPriceCents, event.studentPriceCents, event.guestPriceCents].filter(
    (value): value is number => value != null && value > 0
  );
  if (prices.length === 0) {
    const allZero = [event.memberPriceCents, event.studentPriceCents, event.guestPriceCents].some(
      (value) => value === 0
    );
    return allZero ? 'Free' : null;
  }
  const lowest = Math.min(...prices);
  const formatted = formatPlayEventPriceCents(lowest, region);
  return formatted ? `From ${formatted}` : null;
}

export function formatPlayEventDurationMinutes(startsAt: Date, endsAt: Date): string | null {
  const start = startsAt instanceof Date ? startsAt : new Date(startsAt);
  const end = endsAt instanceof Date ? endsAt : new Date(endsAt);
  const minutes = Math.round((end.getTime() - start.getTime()) / 60_000);
  if (minutes <= 0) return null;
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (remainder === 0) return `${hours} hr`;
  return `${hours} hr ${remainder} min`;
}
